// Linked lead/deal navigation chips for the request drawer header (#1597).
// Renders the funnel-link PUBLIC CODE only (`L-#####` / `D-#####`) — never
// PII — as a clickable chip that deep-links into the lead/deal workspace. The
// lead chip opens the episode page `/leads/<id>` (#1810 retired the lead
// drawer); the deal chip still uses the `?openId=` open-on-mount contract.
// Renders nothing when a code is absent (no empty placeholder).

import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../../../ui/index.js';
import type { RequestView } from '../types.js';

const CHIP_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  height: 22,
  padding: '0 9px',
  borderRadius: 'var(--r-1)',
  border: '1px solid var(--border-1)',
  background: 'var(--bg-1)',
  color: 'var(--fg-2)',
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

function FunnelChip(props: { icon: IconName; code: string; label: string; onClick: () => void }) {
  return (
    <button type="button" aria-label={props.label} onClick={props.onClick} style={CHIP_STYLE}>
      <Icon name={props.icon} size={12} />
      {props.code}
    </button>
  );
}

// A chip renders only when BOTH the FK id and its public code are present;
// a missing code (default read) or null FK (unlinked) yields no link.
function funnelLink(id: string | null | undefined, code: string | null | undefined) {
  return typeof id === 'string' && typeof code === 'string' ? { id, code } : null;
}

export function RequestFunnelChips({ r }: { r: RequestView }) {
  const { t } = useTranslation('requests');
  const navigate = useNavigate();
  const lead = funnelLink(r.leadId, r.leadPublicCode);
  const deal = funnelLink(r.dealId, r.dealPublicCode);
  if (!lead && !deal) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {lead && (
        <FunnelChip
          icon="user"
          code={lead.code}
          label={t('funnel.openLead', { code: lead.code })}
          onClick={() => navigate(`/leads/${encodeURIComponent(lead.id)}`)}
        />
      )}
      {deal && (
        <FunnelChip
          icon="briefcase"
          code={deal.code}
          label={t('funnel.openDeal', { code: deal.code })}
          onClick={() => navigate(`/deals?openId=${encodeURIComponent(deal.id)}`)}
        />
      )}
    </span>
  );
}
